import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { AttendeeCircle } from "@whoosh/design-system/src/components/display";
import { Text, HStack } from "@whoosh/design-system/src/components/primitive";

import { groupSizeAtom } from "../../atoms";
import { EventDetailAttendeesGroupListItemFragment$key } from "./__generated__/EventDetailAttendeesGroupListItemFragment.graphql";

type EventDetailAttendeesGroupListItemProps = {
  fragmentRef: EventDetailAttendeesGroupListItemFragment$key | null;
  playerNounOverride?: string;
};

export const EventDetailAttendeesGroupListItem = ({
  fragmentRef,
  playerNounOverride,
}: EventDetailAttendeesGroupListItemProps) => {
  const data = useFragment(
    EventDetailAttendeesGroupListItemFragmentString,
    fragmentRef
  );

  const maxGroupSize = useAtomValue(groupSizeAtom);

  const registrations = data?.registrations?.edges || [];
  const numGuestRegistrations =
    registrations?.filter(
      (registration) => registration?.node?.type === "GUEST"
    ).length ?? 0;

  const numMemberRegistrations =
    registrations?.filter(
      (registration) => registration?.node?.type === "MEMBER"
    ).length ?? 0;

  return (
    <HStack alignItems="center" py={1}>
      <AttendeeCircle
        maxSize={maxGroupSize}
        currentKnownClients={numMemberRegistrations}
        currentUnknownClients={numGuestRegistrations}
        isPublic={false}
      />
      <HStack flexWrap="wrap" px={4} flex={1}>
        {registrations.map((r, i) => {
          const registration = r?.node;
          const isRegistrantUnknownGuest =
            registration?.type === "GUEST" &&
            !registration?.registrant?.firstName;

          const isRegistrantMemberHold =
            registration?.type === "MEMBER" &&
            !registration?.registrant?.firstName;
          let registrant = "";

          if (isRegistrantUnknownGuest) {
            registrant =
              t`Unknown Guest` +
              `${i + 1 === registrations.length ? "" : ", "}`;
          } else if (isRegistrantMemberHold) {
            registrant =
              t`Member Hold` + `${i + 1 === registrations.length ? "" : ", "}`;
          } else {
            const name =
              registration?.registrant?.firstName &&
              registration?.registrant?.lastName
                ? `${registration?.registrant?.firstName} ${registration?.registrant?.lastName}`
                : t`Unknown ${playerNounOverride ?? "Player"}`;

            registrant = `${name}${i + 1 === registrations.length ? "" : ", "}`;
          }

          return registration?.type === "GUEST" ||
            registration?.type === "MEMBER" ? (
            <HStack alignItems="center" key={i}>
              <Text maxFontSizeMultiplier={2} variant="mainBaseMedium">
                {registrant}
              </Text>
            </HStack>
          ) : (
            <Text maxFontSizeMultiplier={2} key={i} variant="mainBaseMedium">
              {registrant}
            </Text>
          );
        })}
      </HStack>
    </HStack>
  );
};

export const EventDetailAttendeesGroupListItemFragmentString = graphql`
  fragment EventDetailAttendeesGroupListItemFragment on EventSignUp {
    waitlistIndex
    id
    checkedIn
    registrations {
      edges {
        node {
          id
          type
          index
          registrant {
            id
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
          formResponses {
            edges {
              node {
                id
                booleanValue
                stringValue
                transportationType {
                  id
                }
                questionConfiguration {
                  enabled
                  id
                  type
                }
              }
            }
          }
        }
      }
    }
  }
`;
